'use client';

import { useEffect } from 'react';

// Google Analytics / Google Ads - ladowane dopiero po pierwszej interakcji uzytkownika
// (scroll, ruch myszki, dotyk, klawiatura), a nie od razu przy starcie strony.
// To odciaza watek glowny w krytycznym oknie pomiaru TBT (Lighthouse). Ma fallback
// timeout, zeby uzytkownicy czytajacy strone bez ruchu nadal zostali policzeni.
const GA_ID = 'G-W9WSGHNN17';
const GTM_ID = 'GT-5TGZZ2D8';
const INTERACTION_EVENTS = ['scroll', 'mousemove', 'touchstart', 'keydown'] as const;
const FALLBACK_DELAY_MS = 8000;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export default function GtagLoader() {
  useEffect(() => {
    let loaded = false;
    let fallbackTimer: ReturnType<typeof setTimeout>;

    const removeListeners = () => {
      INTERACTION_EVENTS.forEach((event) => window.removeEventListener(event, loadGtag));
      clearTimeout(fallbackTimer);
    };

    function loadGtag() {
      if (loaded) return;
      loaded = true;
      removeListeners();

      // WAZNE: gtag.js rozpoznaje polecenia tylko wtedy, gdy do dataLayer trafia obiekt "arguments"
      // (a nie zwykla tablica z operatora rest). Wczesniej push(args) wrzucal tablice, wiec GA
      // ignorowalo 'config' i nie zliczalo zadnych wizyt. Dodatkowo gtag jest teraz globalne
      // (window.gtag), bo helpery w lib/analytics.ts wolaja window.gtag - wczesniej ono nie istnialo.
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer!.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', GA_ID);
      window.gtag('config', GTM_ID);

      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      script.async = true;
      document.head.appendChild(script);
    }

    INTERACTION_EVENTS.forEach((event) =>
      window.addEventListener(event, loadGtag, { once: true, passive: true })
    );
    fallbackTimer = setTimeout(loadGtag, FALLBACK_DELAY_MS);

    return removeListeners;
  }, []);

  return null;
}
